/**
 * Employer Info Form
 *
 * Form for creating new NIB submission.
 * Note: Employer info (NI number, company name, format) comes from BusinessProfile.
 * Only the contribution period is user-selectable.
 */

import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useBusinessProfile } from '@/hooks/business/useBusinessProfile';

interface SubmissionPeriodFormData {
  contribution_month: string;
  contribution_year: string;
}

interface EmployerInfoFormProps {
  onSubmit: (data: { contribution_period: string }) => void;
  isLoading?: boolean;
}

const MONTHS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 3 }, (_, i) => ({
  value: String(currentYear - 1 + i),
  label: String(currentYear - 1 + i),
}));

export function EmployerInfoForm({
  onSubmit,
  isLoading = false,
}: EmployerInfoFormProps) {
  const { data: profile, isLoading: profileLoading } = useBusinessProfile();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SubmissionPeriodFormData>({
    defaultValues: {
      contribution_month: String(new Date().getMonth() + 1).padStart(2, '0'),
      contribution_year: String(currentYear),
    },
  });

  const formatType = profile?.format_type;

  const handleFormSubmit = (data: SubmissionPeriodFormData) => {
    // Convert to MMYY format
    const period = `${data.contribution_month}${data.contribution_year.slice(-2)}`;
    onSubmit({ contribution_period: period });
  };

  if (profileLoading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-10 bg-gray-200 rounded mb-4"></div>
          <div className="h-10 bg-gray-200 rounded mb-4"></div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <p className="text-yellow-800 mb-4">
          Business profile not found. Please complete your profile setup first.
        </p>
        <Link
          to="/onboarding"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
        >
          Set Up Business Profile
        </Link>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      className="bg-white shadow rounded-lg"
    >
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">New Submission</h3>
      </div>

      <div className="p-6 space-y-6">
        {/* Read-only Employer Info from Business Profile */}
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          <h4 className="text-sm font-medium text-gray-700">
            Employer Information (from your profile)
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">NI Number:</span>{' '}
              <span className="font-medium text-gray-900">
                {profile.employer_ni_number}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Company:</span>{' '}
              <span className="font-medium text-gray-900">
                {profile.company_name}
              </span>
            </div>
            {profile.trading_name && (
              <div>
                <span className="text-gray-500">Trading Name:</span>{' '}
                <span className="font-medium text-gray-900">
                  {profile.trading_name}
                </span>
              </div>
            )}
            <div>
              <span className="text-gray-500">Format:</span>{' '}
              <span className="font-medium text-gray-900">
                {formatType === 'HOSPITALITY'
                  ? 'Hospitality'
                  : 'Non-Hospitality'}
              </span>
            </div>
          </div>
        </div>

        {/* Contribution Period Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Contribution Month */}
          <div>
            <label
              htmlFor="contribution_month"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Contribution Month *
            </label>
            <select
              id="contribution_month"
              {...register('contribution_month', {
                required: 'Month is required',
              })}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              {MONTHS.map(month => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
            {errors.contribution_month && (
              <p className="mt-1 text-sm text-red-600">
                {errors.contribution_month.message}
              </p>
            )}
          </div>

          {/* Contribution Year */}
          <div>
            <label
              htmlFor="contribution_year"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Contribution Year *
            </label>
            <select
              id="contribution_year"
              {...register('contribution_year', {
                required: 'Year is required',
              })}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              {YEARS.map(year => (
                <option key={year.value} value={year.value}>
                  {year.label}
                </option>
              ))}
            </select>
            {errors.contribution_year && (
              <p className="mt-1 text-sm text-red-600">
                {errors.contribution_year.message}
              </p>
            )}
          </div>
        </div>

        {/* Hospitality Format Warning */}
        {formatType === 'HOSPITALITY' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-blue-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  Hospitality format includes gratuity/tips fields and generates
                  115-byte C10 records.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Submit Button */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
        <button
          type="submit"
          disabled={isLoading}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Creating...
            </>
          ) : (
            'Create Submission'
          )}
        </button>
      </div>
    </form>
  );
}

export default EmployerInfoForm;
